import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

const ROOT_TYPE = "checklist";
const CONTAINER_TYPES = new Set([ROOT_TYPE, "list", "section", "itemContainer"]);
const LEAF_ITEM_TYPES = new Set([
	"item",
	"itemYesNo",
	"itemPicker",
	"itemTextInput",
	"itemTextInputMultiline",
	"itemBarcodeScanner",
	"itemChoiceSingle",
	"itemChoiceMulti",
	"itemDate",
	"itemTime",
	"itemDateTime",
	"itemWeather",
	"itemImagePicker",
	"itemSketchPad",
	"itemRanking",
	"itemAddress",
	"itemLocation",
	"itemSelect",
	"itemSearch",
	"itemButton"
]);
const CONTENT_PLAN_KINDS = new Set(["reference", "procedure", "form", "mixed"]);

function isSupportedStructureType(type) {
	return CONTAINER_TYPES.has(type) || LEAF_ITEM_TYPES.has(type);
}

function isLeafItemType(type) {
	return LEAF_ITEM_TYPES.has(type);
}

function canNodeHaveChildren(type) {
	return CONTAINER_TYPES.has(type);
}

function extractStructuredTextCandidate(rawValue = "") {
	if (typeof rawValue !== "string") {
		return rawValue;
	}
	const trimmed = rawValue.trim();
	if (trimmed === "") {
		return "";
	}

	const fenceMatch = trimmed.match(/```(?:json|yaml|yml)?\s*([\s\S]*?)```/i);
	if (fenceMatch && _.isString(fenceMatch[1]) && fenceMatch[1].trim() !== "") {
		return fenceMatch[1].trim();
	}

	const lines = trimmed.split(/\r?\n/);
	const structuredStartIndex = lines.findIndex((line) => /^(?:\s*)(version|operations|op|nodes|children|type|kind|name|label1|label2|title)\s*:|^(?:\s*)-\s+(kind|type)\s*:/.test(line));
	if (structuredStartIndex > 0) {
		return lines.slice(structuredStartIndex).join("\n").trim();
	}

	return trimmed;
}

function tryParseYamlCandidate(candidate) {
	if (!_.isString(candidate) || _.trim(candidate) === "") {
		return null;
	}

	try {
		// Lazy-load to keep this utility usable without forcing YAML parsing unless needed.
		// eslint-disable-next-line global-require
		const YAML = require("yaml");
		if (YAML && typeof YAML.parse === "function") {
			return YAML.parse(candidate);
		}
	} catch (_err) {
		return null;
	}

	return null;
}

function parseStructuredValue(input) {
	if (!_.isString(input)) {
		return input;
	}

	const candidate = extractStructuredTextCandidate(input);
	if (!_.isString(candidate) || _.trim(candidate) === "") {
		return null;
	}

	try {
		return JSON.parse(candidate);
	} catch (_err) {
		let jsonSlice = null;
		const objectStart = candidate.indexOf("{");
		const objectEnd = candidate.lastIndexOf("}");
		const arrayStart = candidate.indexOf("[");
		const arrayEnd = candidate.lastIndexOf("]");
		if (objectStart > -1 && objectEnd > objectStart) {
			jsonSlice = candidate.slice(objectStart, objectEnd + 1);
		} else if (arrayStart > -1 && arrayEnd > arrayStart) {
			jsonSlice = candidate.slice(arrayStart, arrayEnd + 1);
		}
		if (jsonSlice) {
			try {
				return JSON.parse(jsonSlice);
			} catch (_err2) {
				// Fall through to YAML parsing.
			}
		}
	}

	const yamlParsed = tryParseYamlCandidate(candidate);
	if (_.isPlainObject(yamlParsed) || _.isArray(yamlParsed)) {
		return yamlParsed;
	}

	return null;
}

function getFirstStringProp(source, keys, allowEmpty = true) {
	for (let i = 0; i < keys.length; i += 1) {
		const value = _.get(source, keys[i]);
		if (_.isString(value) && (allowEmpty || _.trim(value) !== "")) {
			return value;
		}
	}
	return undefined;
}

function normalizeContentPlanKind(kind = "", fallback = "reference") {
	const lower = _.isString(kind) ? _.trim(kind).toLowerCase() : "";
	switch (lower) {
		case "reference":
		case "procedure":
		case "form":
		case "mixed":
			return lower;
		case "recipe":
		case "howto":
		case "how-to":
		case "instructions":
		case "checklist":
		case "steps":
			return "procedure";
		case "survey":
		case "inspection":
		case "questionnaire":
			return "form";
		default:
			return CONTENT_PLAN_KINDS.has(fallback) ? fallback : "reference";
	}
}

export function inferStructureStudioIntent(prompt = "") {
	const text = _.isString(prompt) ? prompt : `${prompt || ""}`;
	const lower = text.toLowerCase();

	const hasStructuredSpec = !!coerceStructurePatchV1(text) || !!coerceContentPlanV1(text);
	if (hasStructuredSpec) {
		return {
			generationMode: "structuredInput",
			requestedOperation: null,
			allowsLeafSelection: true
		};
	}

	const hasSibling = /\bsibling\b/.test(lower);
	const hasBefore = /\b(before|above|preceding)\b/.test(lower);
	const hasAfter = /\b(after|below|following)\b/.test(lower);
	const hasReplaceChildren = /\breplace\b[\s\S]{0,40}\bchildren\b/.test(lower);
	const hasReplaceNode = /\breplace\b[\s\S]{0,40}\b(selected node|selected item|selected section|selected list|node itself|entire node)\b/.test(lower);
	const hasAppendChildren = /\b(under|inside|within|into|append|add)\b[\s\S]{0,40}\b(child|children|items?|sections?|lists?)\b/.test(lower)
		|| /\bcreate\b[\s\S]{0,40}\bunder the selected\b/.test(lower);
	const mentionsNodeTypes = /\b(list|section|item container|itemcontainer|item picker|itempicker|text input|itemtextinput|yes\/no|itemyesno|label only|labelonly|item)\b/.test(lower);
	const mentionsSelection = /\bselected node|selected item|selected section|selected list|selected\b/.test(lower);
	const hasCreateVerb = /\b(create|add|insert|append|prepend|place|put|make)\b/.test(lower);
	const hasSpecificName = /\b(called|named|title[d]?|name[d]?)\b/.test(lower);
	const hasRevisionVerb = /\b(revise|revision|update|modify|change|reorganize|restructure|convert|transform|refactor|nest|group|wrap|move|split)\b/.test(lower)
		|| /\banother level of hierarchy\b/.test(lower)
		|| /\badd another level\b/.test(lower)
		|| /\bmake\b[\s\S]{0,80}\b(list|section|item container|itemcontainer|checklist|activity)\b/.test(lower)
		|| /\bturn\b[\s\S]{0,80}\binto\b/.test(lower);

	const requestedOperation = hasReplaceChildren
		? "replaceChildren"
		: (hasReplaceNode
			? "replaceNode"
			: ((hasSibling && hasBefore) || hasBefore
				? "insertBefore"
				: ((hasSibling && hasAfter) || hasAfter || hasSibling
					? "insertAfter"
					: (hasAppendChildren ? "appendChildren" : null))));

	const explicitStructureCommand = !!requestedOperation
		|| ((hasCreateVerb || hasSpecificName) && mentionsNodeTypes && mentionsSelection)
		|| ((hasCreateVerb || hasSpecificName) && mentionsNodeTypes && (hasSibling || hasBefore || hasAfter));
	const isRevisionStyleRequest = !explicitStructureCommand && hasRevisionVerb;

	return {
		generationMode: explicitStructureCommand ? "structurePatch" : (isRevisionStyleRequest ? "subtreeRevision" : "contentPlan"),
		requestedOperation,
		allowsLeafSelection: requestedOperation === "insertBefore" || requestedOperation === "insertAfter" || requestedOperation === "replaceNode"
	};
}

function normalizeContentEntry(entry, contextLabel = "Entry") {
	if (_.isString(entry)) {
		return {
			label: entry
		};
	}
	if (!_.isPlainObject(entry)) {
		throw new Error(`${contextLabel} must be a string or object.`);
	}

	const label = getFirstStringProp(entry, ["label", "title", "name", "text", "value"], true);
	if (!_.isString(label)) {
		throw new Error(`${contextLabel} must include a label.`);
	}

	const normalizedEntry = {
		label
	};
	const description = getFirstStringProp(entry, ["description", "subtitle", "label2", "details"], true);
	if (_.isString(description)) {
		normalizedEntry.description = description;
	}
	return normalizedEntry;
}

function normalizeContentFieldType(type = "") {
	const lower = _.isString(type) ? _.trim(type).toLowerCase() : "";
	switch (lower) {
		case "item":
		case "text":
		case "input":
		case "textfield":
		case "textinput":
			return "text";
		case "textarea":
		case "multiline":
		case "paragraph":
		case "longtext":
			return "multiline";
		case "boolean":
		case "yesno":
		case "yes/no":
		case "toggle":
			return "yesNo";
		case "singlechoice":
		case "choice":
		case "radio":
		case "select":
		case "choicesingle":
			return "choiceSingle";
		case "multichoice":
		case "checkbox":
		case "checkboxes":
		case "choicemulti":
			return "choiceMulti";
		case "picker":
			return "picker";
		case "date":
		case "time":
		case "datetime":
		case "address":
		case "location":
		case "search":
			return lower;
		default:
			return "text";
	}
}

function normalizeContentField(field, contextLabel = "Field") {
	if (!_.isPlainObject(field)) {
		throw new Error(`${contextLabel} must be an object.`);
	}

	const label = getFirstStringProp(field, ["label", "title", "name"], true);
	if (!_.isString(label)) {
		throw new Error(`${contextLabel} must include a label.`);
	}

	const normalizedField = {
		label,
		type: normalizeContentFieldType(field.type)
	};
	const description = getFirstStringProp(field, ["description", "subtitle", "label2", "details"], true);
	if (_.isString(description)) {
		normalizedField.description = description;
	}
	if (_.isArray(field.options)) {
		normalizedField.options = field.options.filter((option) => _.isString(option)).map((option) => option);
	}
	return normalizedField;
}

function normalizeContentEntryArray(entries, contextLabel = "entries") {
	if (!_.isArray(entries)) {
		return [];
	}
	return entries.map((entry, index) => normalizeContentEntry(entry, `${contextLabel}[${index}]`));
}

function normalizeContentFieldArray(fields, contextLabel = "fields") {
	if (!_.isArray(fields)) {
		return [];
	}
	return fields.map((field, index) => normalizeContentField(field, `${contextLabel}[${index}]`));
}

function normalizeContentGroup(group, contextLabel = "Group") {
	if (_.isString(group)) {
		return {
			title: group,
			items: []
		};
	}
	if (!_.isPlainObject(group)) {
		throw new Error(`${contextLabel} must be a string or object.`);
	}

	const title = getFirstStringProp(group, ["title", "name", "label"], true);
	if (!_.isString(title)) {
		throw new Error(`${contextLabel} must include a title.`);
	}

	return {
		title,
		items: normalizeContentEntryArray(group.items || group.entries || group.bullets, `${contextLabel}.items`),
		steps: normalizeContentEntryArray(group.steps, `${contextLabel}.steps`),
		fields: normalizeContentFieldArray(group.fields, `${contextLabel}.fields`)
	};
}

function normalizeContentSection(section, contextLabel = "Section") {
	if (_.isString(section)) {
		return {
			title: section,
			groups: [],
			items: [],
			steps: [],
			fields: []
		};
	}
	if (!_.isPlainObject(section)) {
		throw new Error(`${contextLabel} must be a string or object.`);
	}

	const title = getFirstStringProp(section, ["title", "name", "label"], true);
	if (!_.isString(title)) {
		throw new Error(`${contextLabel} must include a title.`);
	}

	const normalizedSection = {
		title,
		groups: _.isArray(section.groups) ? section.groups.map((group, index) => normalizeContentGroup(group, `${contextLabel}.groups[${index}]`)) : [],
		items: normalizeContentEntryArray(section.items || section.entries || section.bullets, `${contextLabel}.items`),
		steps: normalizeContentEntryArray(section.steps, `${contextLabel}.steps`),
		fields: normalizeContentFieldArray(section.fields, `${contextLabel}.fields`)
	};

	const intro = getFirstStringProp(section, ["intro", "description", "summary"], true);
	if (_.isString(intro) && _.trim(intro) !== "") {
		normalizedSection.intro = intro;
	}

	return normalizedSection;
}

function normalizeStructureKind(kind = "", rawType = "") {
	const lowerKind = _.isString(kind) ? _.trim(kind).toLowerCase() : "";
	const trimmedType = _.isString(rawType) ? _.trim(rawType) : "";

	switch (lowerKind) {
		case "":
			return trimmedType;
		case "activity":
		case "checklist":
			return ROOT_TYPE;
		case "list":
			return "list";
		case "section":
			return "section";
		case "itemcontainer":
			return "itemContainer";
		case "item":
			if (trimmedType && isLeafItemType(trimmedType)) {
				return trimmedType;
			}
			return "item";
		default:
			return trimmedType || kind;
	}
}

function normalizeStructureNodeSpec(node, contextLabel = "Node") {
	if (!_.isPlainObject(node)) {
		throw new Error(`${contextLabel} must be an object.`);
	}

	const normalizedType = normalizeStructureKind(node.kind, node.type);
	if (!_.isString(normalizedType) || _.trim(normalizedType) === "") {
		throw new Error(`${contextLabel} must include a type or kind.`);
	}

	const normalizedNode = {
		type: normalizedType
	};

	if (_.isString(node.id)) {
		normalizedNode.id = node.id;
	}
	if (_.isString(node.guid) && _.trim(node.guid) !== "") {
		normalizedNode.guid = node.guid;
	}

	if (normalizedType === ROOT_TYPE || normalizedType === "list" || normalizedType === "section") {
		const normalizedName = getFirstStringProp(node, ["name", "title", "label", "label1"], true);
		if (!_.isString(normalizedName)) {
			throw new Error(`${contextLabel} must include a name.`);
		}
		normalizedNode.name = normalizedName;
		normalizedNode.children = _.isArray(node.children)
			? node.children.map((childNode, index) => normalizeStructureNodeSpec(childNode, `${contextLabel}.children[${index}]`))
			: [];
		return normalizedNode;
	}

	const normalizedLabel1 = getFirstStringProp(node, ["label1", "label", "name", "title"], true);
	if (!_.isString(normalizedLabel1)) {
		throw new Error(`${contextLabel} must include a label1.`);
	}
	normalizedNode.label1 = normalizedLabel1;

	const normalizedLabel2 = getFirstStringProp(node, ["label2", "description", "subtitle"], true);
	if (_.isString(normalizedLabel2)) {
		normalizedNode.label2 = normalizedLabel2;
	}

	if (normalizedType === "itemContainer") {
		normalizedNode.children = _.isArray(node.children)
			? node.children.map((childNode, index) => normalizeStructureNodeSpec(childNode, `${contextLabel}.children[${index}]`))
			: [];
		return normalizedNode;
	}

	if (node.hasOwnProperty("labelOnly")) {
		normalizedNode.labelOnly = node.labelOnly;
	}

	return normalizedNode;
}

function normalizeStructureNodesSpec(nodes, contextLabel = "nodes") {
	if (_.isPlainObject(nodes) && (nodes.kind || nodes.type)) {
		return [normalizeStructureNodeSpec(nodes, `${contextLabel}[0]`)];
	}
	if (!_.isArray(nodes)) {
		throw new Error(`${contextLabel} must be an array of nodes.`);
	}
	return nodes.map((node, index) => normalizeStructureNodeSpec(node, `${contextLabel}[${index}]`));
}

function normalizePatchTarget(target, defaultTarget = { mode: "selected" }) {
	if (_.isPlainObject(target) && _.isString(target.mode) && _.trim(target.mode) !== "") {
		return target;
	}
	if (_.isString(target) && _.trim(target) !== "") {
		return { mode: target.trim() };
	}
	return defaultTarget;
}

export function treePathToChecklistPath(treePath = []) {
	const checklistPath = [];
	for (let i = 0; i < treePath.length; i += 1) {
		if (treePath[i] === "children" && typeof treePath[i + 1] === "number") {
			checklistPath.push(treePath[i + 1]);
			i += 1;
		}
	}
	return checklistPath;
}

export function checklistPathToTreePath(checklistPath = []) {
	const treePath = ["tree", "root"];
	if (!_.isArray(checklistPath)) {
		return treePath;
	}
	checklistPath.forEach((index) => {
		if (Number.isInteger(index) && index >= 0) {
			treePath.push("children", index);
		}
	});
	return treePath;
}

export function getStructureNodeAtPath(structureNode, checklistPath = []) {
	let currentNode = structureNode;
	for (let i = 0; i < checklistPath.length; i += 1) {
		const index = checklistPath[i];
		const childNodes = _.isArray(currentNode && currentNode.children) ? currentNode.children : [];
		if (index < 0 || index >= childNodes.length) {
			return null;
		}
		currentNode = childNodes[index];
	}
	return currentNode;
}

function normalizePatchOperationSpec(operation, index = 0, defaultTarget = { mode: "selected" }) {
	if (!_.isPlainObject(operation)) {
		throw new Error(`operations[${index}] must be an object.`);
	}

	const normalizedOp = _.isString(operation.op) && _.trim(operation.op) !== ""
		? operation.op.trim()
		: "appendChildren";
	const rawNodes = operation.hasOwnProperty("nodes")
		? operation.nodes
		: (operation.hasOwnProperty("node") ? [operation.node] : null);
	const normalizedNodes = normalizeStructureNodesSpec(rawNodes, `operations[${index}].nodes`);

	return {
		op: normalizedOp,
		target: normalizePatchTarget(operation.target, defaultTarget),
		nodes: normalizedNodes
	};
}

export function coerceStructurePatchV1(input, options = {}) {
	const defaultTarget = _.isPlainObject(options.defaultTarget) ? options.defaultTarget : { mode: "selected" };
	const throwOnError = options.throwOnError === true;

	try {
		const parsed = parseStructuredValue(input);
		if (!parsed) {
			return null;
		}

		if (_.isPlainObject(parsed) && parsed.version === 1 && _.isArray(parsed.operations)) {
			return {
				version: 1,
				operations: parsed.operations.map((operation, index) => normalizePatchOperationSpec(operation, index, defaultTarget))
			};
		}

		if (_.isArray(parsed)) {
			const looksLikeOperations = parsed.every((entry) => _.isPlainObject(entry) && (_.isString(entry.op) || entry.hasOwnProperty("nodes")));
			if (looksLikeOperations) {
				return {
					version: 1,
					operations: parsed.map((operation, index) => normalizePatchOperationSpec(operation, index, defaultTarget))
				};
			}
			return {
				version: 1,
				operations: [{
					op: "appendChildren",
					target: defaultTarget,
					nodes: normalizeStructureNodesSpec(parsed, "operations[0].nodes")
				}]
			};
		}

		if (!_.isPlainObject(parsed)) {
			return null;
		}

		const parsedRootType = normalizeStructureKind(parsed.kind, parsed.type);
		if (parsedRootType === ROOT_TYPE && _.isArray(parsed.children)) {
			return {
				version: 1,
				operations: [{
					op: "appendChildren",
					target: defaultTarget,
					nodes: normalizeStructureNodesSpec(parsed.children, "operations[0].nodes")
				}]
			};
		}

		if (parsed.hasOwnProperty("operations") || parsed.hasOwnProperty("op")) {
			return {
				version: 1,
				operations: [normalizePatchOperationSpec(parsed, 0, defaultTarget)]
			};
		}

		if (parsed.hasOwnProperty("nodes")) {
			return {
				version: 1,
				operations: [{
					op: _.isString(parsed.op) && _.trim(parsed.op) !== "" ? parsed.op.trim() : "appendChildren",
					target: normalizePatchTarget(parsed.target, defaultTarget),
					nodes: normalizeStructureNodesSpec(parsed.nodes, "operations[0].nodes")
				}]
			};
		}

		if (parsed.kind || parsed.type) {
			return {
				version: 1,
				operations: [{
					op: "appendChildren",
					target: defaultTarget,
					nodes: normalizeStructureNodesSpec([parsed], "operations[0].nodes")
				}]
			};
		}

		return null;
	} catch (err) {
		if (throwOnError) {
			throw err;
		}
		return null;
	}
}

export function coerceContentPlanV1(input, options = {}) {
	const defaultKind = normalizeContentPlanKind(options.defaultKind || "reference");
	const throwOnError = options.throwOnError === true;

	try {
		let parsed = parseStructuredValue(input);
		if (!parsed) {
			return null;
		}

		if (_.isPlainObject(parsed) && _.isPlainObject(parsed.plan)) {
			parsed = parsed.plan;
		}

		if (_.isArray(parsed)) {
			return {
				version: 1,
				title: _.isString(options.defaultTitle) ? options.defaultTitle : "",
				kind: defaultKind,
				sections: parsed.map((section, index) => normalizeContentSection(section, `sections[${index}]`))
			};
		}

		if (!_.isPlainObject(parsed)) {
			return null;
		}

		const normalizedPlan = {
			version: 1,
			title: getFirstStringProp(parsed, ["title", "name"], true) || (_.isString(options.defaultTitle) ? options.defaultTitle : ""),
			kind: normalizeContentPlanKind(parsed.kind || parsed.type, defaultKind),
			sections: []
		};

		if (_.isArray(parsed.sections)) {
			normalizedPlan.sections = parsed.sections.map((section, index) => normalizeContentSection(section, `sections[${index}]`));
			return normalizedPlan;
		}

		if (_.isArray(parsed.groups) || _.isArray(parsed.items) || _.isArray(parsed.steps) || _.isArray(parsed.fields)) {
			normalizedPlan.sections = [normalizeContentSection({
				title: normalizedPlan.title || "Generated Content",
				groups: parsed.groups,
				items: parsed.items,
				steps: parsed.steps,
				fields: parsed.fields,
				intro: parsed.intro || parsed.description || parsed.summary
			}, "sections[0]")];
			return normalizedPlan;
		}

		return null;
	} catch (err) {
		if (throwOnError) {
			throw err;
		}
		return null;
	}
}

export function coerceSubtreeSpecV1(input, options = {}) {
	const throwOnError = options.throwOnError === true;

	try {
		let parsed = parseStructuredValue(input);
		if (!parsed) {
			return null;
		}

		if (_.isPlainObject(parsed) && _.isPlainObject(parsed.subtree)) {
			parsed = parsed.subtree;
		}

		if (_.isArray(parsed)) {
			return {
				version: 1,
				nodes: normalizeStructureNodesSpec(parsed, "nodes")
			};
		}

		if (!_.isPlainObject(parsed)) {
			return null;
		}

		if (_.isPlainObject(parsed.checklist)) {
			return {
				version: 1,
				checklist: normalizeStructureNodeSpec(parsed.checklist, "checklist")
			};
		}

		if (_.isArray(parsed.nodes)) {
			return {
				version: 1,
				nodes: normalizeStructureNodesSpec(parsed.nodes, "nodes")
			};
		}

		if (_.isPlainObject(parsed.node)) {
			return {
				version: 1,
				nodes: normalizeStructureNodesSpec([parsed.node], "nodes")
			};
		}

		if (parsed.kind || parsed.type) {
			const normalizedType = normalizeStructureKind(parsed.kind, parsed.type);
			if (normalizedType === ROOT_TYPE) {
				return {
					version: 1,
					checklist: normalizeStructureNodeSpec(parsed, "checklist")
				};
			}
			return {
				version: 1,
				nodes: normalizeStructureNodesSpec([parsed], "nodes")
			};
		}

		return null;
	} catch (err) {
		if (throwOnError) {
			throw err;
		}
		return null;
	}
}

function mapFieldTypeToStructureItemType(fieldType = "text") {
	switch (fieldType) {
		case "multiline":
			return "itemTextInputMultiline";
		case "yesNo":
			return "itemYesNo";
		case "choiceSingle":
			return "itemChoiceSingle";
		case "choiceMulti":
			return "itemChoiceMulti";
		case "picker":
			return "itemPicker";
		case "date":
			return "itemDate";
		case "time":
			return "itemTime";
		case "datetime":
			return "itemDateTime";
		case "address":
			return "itemAddress";
		case "location":
			return "itemLocation";
		case "search":
			return "itemSearch";
		case "text":
		default:
			return "itemTextInput";
	}
}

function createLabelOnlyItem(label, label2 = undefined) {
	const node = {
		type: "item",
		label1: label,
		labelOnly: true
	};
	if (_.isString(label2) && label2 !== "") {
		node.label2 = label2;
	}
	return node;
}

function createChecklistItem(label, label2 = undefined) {
	const node = {
		type: "item",
		label1: label
	};
	if (_.isString(label2) && label2 !== "") {
		node.label2 = label2;
	}
	return node;
}

function createItemContainerNode(label1, children = [], label2 = undefined) {
	const node = {
		type: "itemContainer",
		label1,
		children
	};
	if (_.isString(label2) && label2 !== "") {
		node.label2 = label2;
	}
	return node;
}

function createFieldItem(field) {
	const node = {
		type: mapFieldTypeToStructureItemType(field.type),
		label1: field.label
	};
	if (_.isString(field.description) && field.description !== "") {
		node.label2 = field.description;
	}
	return node;
}

function mapContentEntriesToStructureNodes(entries, effectiveProfile, entryMode = "item") {
	const nodes = [];
	entries.forEach((entry) => {
		if (effectiveProfile === "form" && entryMode !== "step") {
			nodes.push(createFieldItem({
				label: entry.label,
				description: entry.description,
				type: "text"
			}));
		} else {
			nodes.push(createChecklistItem(entry.label, entry.description));
		}
	});
	return nodes;
}

function buildStructureChildrenFromContentBucket(bucket, effectiveProfile, options = {}) {
	const children = [];
	const includeIntroAsLabel = options.includeIntroAsLabel !== false;

	if (includeIntroAsLabel && _.isString(bucket.intro) && _.trim(bucket.intro) !== "") {
		children.push(createLabelOnlyItem(bucket.intro));
	}

	children.push(...mapContentEntriesToStructureNodes(bucket.items || [], effectiveProfile, "item"));
	children.push(...(bucket.fields || []).map((field) => createFieldItem(field)));
	children.push(...mapContentEntriesToStructureNodes(bucket.steps || [], effectiveProfile, "step"));

	return children;
}

function buildStructureChildrenFromContentSection(section, effectiveProfile) {
	const children = [];

	if (_.isString(section.intro) && _.trim(section.intro) !== "") {
		children.push(createLabelOnlyItem(section.intro));
	}

	section.groups.forEach((group) => {
		children.push(createItemContainerNode(
			group.title,
			buildStructureChildrenFromContentBucket(group, effectiveProfile, { includeIntroAsLabel: true })
		));
	});

	children.push(...buildStructureChildrenFromContentBucket(section, effectiveProfile, { includeIntroAsLabel: false }));

	return children;
}

function createSectionNode(name, children = []) {
	return {
		type: "section",
		name,
		children
	};
}

function createListNode(name, children = []) {
	return {
		type: "list",
		name,
		children
	};
}

function cloneStructureNode(node = {}) {
	return _.cloneDeep(node);
}

function isHeadingLikeItem(node = {}) {
	return !!(
		node &&
		node.type === "item" &&
		_.isString(node.label1) &&
		_.trim(node.label1) !== "" &&
		!node.label2 &&
		node.labelOnly !== true
	);
}

function isStepLikeLabel(label = "") {
	return _.isString(label) && /^step\s*\d+/i.test(_.trim(label));
}

function isSingleLeafStepGroup(node = {}) {
	const nodeType = _.get(node, "type", "");
	const groupLabel = nodeType === "section" ? node.name : node.label1;
	return !!(
		node &&
		(nodeType === "itemContainer" || nodeType === "section") &&
		Array.isArray(node.children) &&
		node.children.length === 1 &&
		isLeafItemType(_.get(node, "children[0].type", "")) &&
		isStepLikeLabel(groupLabel)
	);
}

function getDefaultSectionNameForList(listName = "") {
	const lower = _.isString(listName) ? _.trim(listName).toLowerCase() : "";
	if (/(instruction|direction|preparation|method|procedure|steps?)/.test(lower)) {
		return "Steps";
	}
	return "Items";
}

function convertItemContainerToSection(node = {}) {
	const normalizedChildren = Array.isArray(node.children) ? node.children.map((childNode) => cloneStructureNode(childNode)) : [];
	return {
		type: "section",
		name: _.isString(node.label1) && _.trim(node.label1) !== "" ? node.label1 : "Section",
		children: normalizedChildren
	};
}

function normalizeListChildrenToSections(children = [], listName = "") {
	if (!Array.isArray(children) || children.length === 0) {
		return [];
	}

	const allStepContainers = children.every((childNode) => isSingleLeafStepGroup(childNode));
	if (allStepContainers) {
		return [{
			type: "section",
			name: "Steps",
			children: children.map((childNode) => cloneStructureNode(childNode.children[0]))
		}];
	}

	const nextSections = [];
	let looseItems = [];
	const flushLooseItems = () => {
		if (looseItems.length === 0) {
			return;
		}
		nextSections.push({
			type: "section",
			name: getDefaultSectionNameForList(listName),
			children: looseItems
		});
		looseItems = [];
	};

	children.forEach((childNode) => {
		if (!childNode || typeof childNode !== "object") {
			return;
		}
		if (childNode.type === "section") {
			flushLooseItems();
			nextSections.push(cloneStructureNode(childNode));
			return;
		}
		if (childNode.type === "itemContainer") {
			flushLooseItems();
			nextSections.push(convertItemContainerToSection(childNode));
			return;
		}
		if (isLeafItemType(childNode.type)) {
			looseItems.push(cloneStructureNode(childNode));
		}
	});

	flushLooseItems();
	return nextSections;
}

function shouldPromoteHeadingRunToList(runNodes = []) {
	if (!Array.isArray(runNodes) || runNodes.length === 0) {
		return false;
	}
	return runNodes.every((node) => node && (node.type === "itemContainer" || node.type === "section" || isLeafItemType(node.type)));
}

function normalizeChecklistChildren(children = []) {
	if (!Array.isArray(children) || children.length === 0) {
		return [];
	}

	const nextChildren = [];
	for (let index = 0; index < children.length; index += 1) {
		const childNode = children[index];
		if (isHeadingLikeItem(childNode)) {
			const runNodes = [];
			let nextIndex = index + 1;
			while (nextIndex < children.length && !isHeadingLikeItem(children[nextIndex])) {
				runNodes.push(children[nextIndex]);
				nextIndex += 1;
			}
			if (shouldPromoteHeadingRunToList(runNodes)) {
				nextChildren.push({
					type: "list",
					name: childNode.label1,
					children: normalizeListChildrenToSections(runNodes, childNode.label1)
				});
				index = nextIndex - 1;
				continue;
			}
		}

		if (childNode && childNode.type === "itemContainer") {
			nextChildren.push(convertItemContainerToSection(childNode));
			continue;
		}

		nextChildren.push(cloneStructureNode(childNode));
	}

	return nextChildren;
}

function normalizeGeneratedStructureNode(node = {}, parentType = null) {
	if (!node || typeof node !== "object") {
		return node;
	}

	const normalizedNode = cloneStructureNode(node);
	const childNodes = Array.isArray(normalizedNode.children)
		? normalizedNode.children.map((childNode) => normalizeGeneratedStructureNode(childNode, normalizedNode.type))
		: [];

	if (normalizedNode.type === ROOT_TYPE) {
		normalizedNode.children = normalizeChecklistChildren(childNodes);
		return normalizedNode;
	}

	if (normalizedNode.type === "list") {
		normalizedNode.children = normalizeListChildrenToSections(childNodes, normalizedNode.name);
		return normalizedNode;
	}

	if (normalizedNode.type === "itemContainer" && (parentType === ROOT_TYPE || parentType === "list")) {
		return convertItemContainerToSection({
			...normalizedNode,
			children: childNodes
		});
	}

	if (canNodeHaveChildren(normalizedNode.type)) {
		normalizedNode.children = childNodes;
	}

	return normalizedNode;
}

function shouldPromoteSingleRootListToChecklist(normalizedNodes = [], options = {}) {
	const selectedType = normalizeParentStructureType(options.selectedType || "");
	const generationMode = _.isString(options.generationMode) ? _.trim(options.generationMode) : "";
	if (selectedType !== ROOT_TYPE || (generationMode !== "contentPlan" && generationMode !== "subtreeRevision")) {
		return false;
	}
	if (!Array.isArray(normalizedNodes) || normalizedNodes.length !== 1) {
		return false;
	}
	const firstNode = normalizedNodes[0];
	return !!(
		firstNode &&
		firstNode.type === "list" &&
		Array.isArray(firstNode.children) &&
		firstNode.children.length > 0 &&
		firstNode.children.every((childNode) => childNode && childNode.type === "section")
	);
}

function shouldWrapRootNodesToChecklist(normalizedNodes = [], options = {}) {
	const selectedType = normalizeParentStructureType(options.selectedType || "");
	const generationMode = _.isString(options.generationMode) ? _.trim(options.generationMode) : "";
	if (selectedType !== ROOT_TYPE || generationMode !== "subtreeRevision") {
		return false;
	}
	if (!Array.isArray(normalizedNodes) || normalizedNodes.length === 0) {
		return false;
	}
	try {
		compileNodesForParent(normalizedNodes, ROOT_TYPE, "nodes");
		return true;
	} catch (_err) {
		return false;
	}
}

export function normalizeGeneratedSubtreeSpecV1(subtreeSpec = null, options = {}) {
	if (!subtreeSpec || typeof subtreeSpec !== "object") {
		return null;
	}

	if (_.isPlainObject(subtreeSpec.checklist)) {
		return {
			version: 1,
			checklist: normalizeGeneratedStructureNode(subtreeSpec.checklist, null)
		};
	}

	if (Array.isArray(subtreeSpec.nodes)) {
		const normalizedNodes = normalizeChecklistChildren(subtreeSpec.nodes.map((node) => normalizeGeneratedStructureNode(node, ROOT_TYPE)));
		if (shouldPromoteSingleRootListToChecklist(normalizedNodes, options)) {
			const promotedList = normalizedNodes[0];
			return {
				version: 1,
				checklist: {
					type: "checklist",
					name: promotedList.name || "Generated Activity",
					children: promotedList.children
				}
			};
		}
		if (shouldWrapRootNodesToChecklist(normalizedNodes, options)) {
			return {
				version: 1,
				checklist: {
					type: "checklist",
					name: (_.isString(options.selectedName) && _.trim(options.selectedName) !== "")
						? options.selectedName
						: "Generated Activity",
					children: normalizedNodes
				}
			};
		}
		return {
			version: 1,
			nodes: normalizedNodes
		};
	}

	return subtreeSpec;
}

function getDefaultNestedSectionName(section) {
	const hasItems = _.isArray(section.items) && section.items.length > 0;
	const hasFields = _.isArray(section.fields) && section.fields.length > 0;
	const hasSteps = _.isArray(section.steps) && section.steps.length > 0;
	const hasIntro = _.isString(section.intro) && _.trim(section.intro) !== "";

	if (hasSteps && !hasItems && !hasFields && !hasIntro) {
		return "Steps";
	}
	if (hasFields && !hasItems && !hasSteps && !hasIntro) {
		return "Fields";
	}
	if (hasItems && !hasFields && !hasSteps && !hasIntro) {
		return "Items";
	}
	return "Details";
}

function buildNestedSectionsForTopLevelSection(section, effectiveProfile) {
	const nestedSections = [];

	(section.groups || []).forEach((group) => {
		nestedSections.push(createSectionNode(
			group.title,
			buildStructureChildrenFromContentBucket(group, effectiveProfile, { includeIntroAsLabel: false })
		));
	});

	const remainingChildren = buildStructureChildrenFromContentBucket(section, effectiveProfile, { includeIntroAsLabel: true });
	if (remainingChildren.length > 0 || nestedSections.length === 0) {
		nestedSections.push(createSectionNode(getDefaultNestedSectionName(section), remainingChildren));
	}

	return nestedSections;
}

function normalizeParentStructureType(type = ROOT_TYPE) {
	const lower = _.isString(type) ? _.trim(type).toLowerCase() : ROOT_TYPE;
	if (lower === "itemcontainer") {
		return "itemContainer";
	}
	if (lower === "checklist" || lower === "list" || lower === "section" || lower === "itemcontainer") {
		return lower === "itemcontainer" ? "itemContainer" : lower;
	}
	return ROOT_TYPE;
}

export function contentPlanToStructurePatchV1(planInput, options = {}) {
	const normalizedPlan = coerceContentPlanV1(planInput, {
		defaultKind: options.contentProfile || "reference",
		throwOnError: true
	});
	const parentType = normalizeParentStructureType(options.parentType || ROOT_TYPE);
	const explicitProfile = _.isString(options.contentProfile) ? _.trim(options.contentProfile).toLowerCase() : "auto";
	const effectiveProfile = explicitProfile && explicitProfile !== "auto"
		? normalizeContentPlanKind(explicitProfile, normalizedPlan.kind)
		: normalizeContentPlanKind(normalizedPlan.kind, "reference");

	const sections = _.isArray(normalizedPlan.sections) ? normalizedPlan.sections : [];
	let nodes = [];
	const preferListsAtRoot = parentType === ROOT_TYPE && sections.length > 0 && (
		sections.length > 1 ||
		sections.some((section) => (_.isArray(section.groups) && section.groups.length > 0))
	);

	if (preferListsAtRoot) {
		nodes = sections.map((section) => createListNode(
			section.title || normalizedPlan.title || "Generated Content",
			buildNestedSectionsForTopLevelSection(section, effectiveProfile)
		));
	} else if (parentType === ROOT_TYPE || parentType === "list") {
		const normalizedSections = sections.length > 0 ? sections : [{
			title: normalizedPlan.title || "Generated Content",
			groups: [],
			items: [],
			steps: [],
			fields: []
		}];
		nodes = normalizedSections.map((section) => createSectionNode(
			section.title || normalizedPlan.title || "Generated Content",
			buildStructureChildrenFromContentSection(section, effectiveProfile)
		));
	} else {
		const includeSectionHeaders = sections.length > 1 || (sections.length === 1 && sections[0].title && sections[0].title !== normalizedPlan.title);
		sections.forEach((section) => {
			if (includeSectionHeaders && _.isString(section.title) && _.trim(section.title) !== "") {
				nodes.push(createItemContainerNode(
					section.title,
					buildStructureChildrenFromContentSection(section, effectiveProfile)
				));
				return;
			}
			nodes.push(...buildStructureChildrenFromContentSection(section, effectiveProfile));
		});
	}

	return {
		version: 1,
		operations: [{
			op: "appendChildren",
			target: _.isPlainObject(options.defaultTarget) ? options.defaultTarget : { mode: "selected" },
			nodes
		}]
	};
}

function getNormalizedSubtreeNodes(subtreeSpec = null) {
	if (!subtreeSpec || typeof subtreeSpec !== "object") {
		return [];
	}
	if (Array.isArray(subtreeSpec.nodes)) {
		return subtreeSpec.nodes;
	}
	return [];
}

function buildStructureApplyChoice({ key, label, description, op = "", recommended = false }) {
	return {
		key,
		label,
		description,
		recommended,
		op
	};
}

function canCompileNodesForParent(nodes, parentType, options = {}) {
	try {
		compileNodesForParent(nodes, parentType, "nodes", options);
		return true;
	} catch (_err) {
		return false;
	}
}

export function resolveStructureStudioApplyChoices(selection = {}, subtreeSpec = null) {
	const selectedType = normalizeParentStructureType(selection.selectedType || ROOT_TYPE);
	const parentType = normalizeParentStructureType(selection.parentType || "");
	const generationMode = _.isString(selection.generationMode) ? _.trim(selection.generationMode) : "";
	const selectedPath = Array.isArray(selection.selectedPath) ? selection.selectedPath : [];
	const hasParent = selectedPath.length > 0 && !!parentType;

	if (subtreeSpec && _.isPlainObject(subtreeSpec.checklist)) {
		try {
			compileStructureNodeForChecklist(subtreeSpec.checklist, null, "checklist");
		} catch (_err) {
			return [];
		}
		return [
			buildStructureApplyChoice({
				key: "replaceActivity",
				label: "Replace Activity",
				description: "Replace the current activity with the generated checklist.",
				recommended: true
			})
		];
	}

	const nodes = getNormalizedSubtreeNodes(subtreeSpec);
	if (!Array.isArray(nodes) || nodes.length === 0) {
		return [];
	}

	const canReplaceSelected = nodes.length === 1 &&
		selectedType !== ROOT_TYPE &&
		nodes[0].type === selectedType &&
		hasParent &&
		canCompileNodesForParent(nodes, parentType || ROOT_TYPE, {
			existingChildren: parentType === ROOT_TYPE ? selection.parentChildren : []
		});
	const canReplaceChildren = generationMode === "subtreeRevision" &&
		canNodeHaveChildren(selectedType) &&
		canCompileNodesForParent(nodes, selectedType);
	const canAppendChildren = canNodeHaveChildren(selectedType) &&
		canCompileNodesForParent(nodes, selectedType, {
			existingChildren: selectedType === ROOT_TYPE ? selection.selectedChildren : []
		});
	const canInsertAsSiblings = hasParent &&
		canCompileNodesForParent(nodes, parentType || ROOT_TYPE, {
			existingChildren: parentType === ROOT_TYPE ? selection.parentChildren : []
		});

	const choices = [];
	if (canReplaceSelected) {
		choices.push(buildStructureApplyChoice({
			key: "replaceSelected",
			label: "Replace Selected",
			description: "Replace the selected node with the generated revised node.",
			recommended: true,
			op: "replaceNode"
		}));
	}
	if (canReplaceChildren) {
		choices.push(buildStructureApplyChoice({
			key: "replaceChildren",
			label: selectedType === ROOT_TYPE ? "Replace Activity Children" : "Replace Children",
			description: selectedType === ROOT_TYPE
				? "Replace the current activity's top-level children with the generated nodes."
				: "Replace the selected node's children with the generated nodes.",
			recommended: !canReplaceSelected,
			op: "replaceChildren"
		}));
	}
	if (canAppendChildren) {
		choices.push(buildStructureApplyChoice({
			key: "appendChildren",
			label: selectedType === ROOT_TYPE ? "Append to Activity" : "Append as Children",
			description: selectedType === ROOT_TYPE
				? "Append the generated nodes to the activity root."
				: "Append the generated nodes as children of the selected node.",
			recommended: !canReplaceSelected && !canReplaceChildren && (!canInsertAsSiblings || selectedType === ROOT_TYPE || selectedType === "list" || selectedType === "section" || selectedType === "itemContainer"),
			op: "appendChildren"
		}));
	}
	if (canInsertAsSiblings) {
		choices.push(buildStructureApplyChoice({
			key: "insertAfter",
			label: "Insert After Selected",
			description: "Insert the generated nodes as siblings after the selected node.",
			recommended: !canReplaceSelected && !canAppendChildren,
			op: "insertAfter"
		}));
		choices.push(buildStructureApplyChoice({
			key: "insertBefore",
			label: "Insert Before Selected",
			description: "Insert the generated nodes as siblings before the selected node.",
			recommended: false,
			op: "insertBefore"
		}));
	}

	if (choices.length === 0) {
		return [];
	}

	if (!choices.some((choice) => choice.recommended)) {
		choices[0].recommended = true;
	}

	return choices;
}

export function buildStructurePatchFromSubtreeSpec(subtreeSpec = null, applyChoice = null) {
	if (!subtreeSpec || !applyChoice || typeof applyChoice !== "object") {
		throw new Error("Subtree spec and apply choice are required.");
	}

	if (applyChoice.key === "replaceActivity") {
		if (!subtreeSpec.checklist) {
			throw new Error("Replace Activity requires a checklist subtree.");
		}
		return {
			kind: "replaceActivity",
			checklist: compileStructureNodeForChecklist(subtreeSpec.checklist, null, "checklist")
		};
	}

	const nodes = getNormalizedSubtreeNodes(subtreeSpec);
	if (!Array.isArray(nodes) || nodes.length === 0) {
		throw new Error("Generated subtree must include one or more nodes.");
	}

	return {
		kind: "patch",
		patch: {
			version: 1,
			operations: [{
				op: applyChoice.op || "appendChildren",
				target: { mode: "selected" },
				nodes
			}]
		}
	};
}

export function applySubtreeSpecV1(checklist = {}, subtreeSpec = null, applyChoice = null, options = {}) {
	const compiled = buildStructurePatchFromSubtreeSpec(subtreeSpec, applyChoice);
	if (compiled.kind === "replaceActivity") {
		const preservedChecklist = _.isPlainObject(checklist) ? _.cloneDeep(checklist) : {};
		const rawGeneratedChecklist = subtreeSpec && _.isPlainObject(subtreeSpec.checklist) ? subtreeSpec.checklist : {};
		const generatedChecklist = compiled.checklist || {};
		const preserveExistingId = !(_.isString(rawGeneratedChecklist.id) && _.trim(rawGeneratedChecklist.id) !== "");
		const preserveExistingGuid = !(_.isString(rawGeneratedChecklist.guid) && _.trim(rawGeneratedChecklist.guid) !== "");

		return {
			...preservedChecklist,
			...generatedChecklist,
			type: ROOT_TYPE,
			name: generatedChecklist.name,
			children: generatedChecklist.children,
			id: preserveExistingId ? preservedChecklist.id : generatedChecklist.id,
			guid: preserveExistingGuid ? preservedChecklist.guid : generatedChecklist.guid
		};
	}
	return applyStructurePatchV1(checklist, compiled.patch, options);
}

function getRootChildStructuralClass(type = "") {
	if (type === "list") {
		return "list";
	}
	if (type === "section") {
		return "section";
	}
	if (isLeafItemType(type)) {
		return "item";
	}
	return "";
}

function getAllowedChildTypes(parentType, options = {}) {
	switch (parentType) {
		case ROOT_TYPE: {
			const existingChildren = Array.isArray(options.existingChildren) ? options.existingChildren : [];
			if (existingChildren.length > 0) {
				const rootClass = getRootChildStructuralClass(_.get(existingChildren, "[0].type", ""));
				if (rootClass === "list") {
					return new Set(["list"]);
				}
				if (rootClass === "section") {
					return new Set(["section"]);
				}
				if (rootClass === "item") {
					return new Set([...LEAF_ITEM_TYPES]);
				}
			}
			return new Set(["list", "section", ...LEAF_ITEM_TYPES]);
		}
		case "list":
			return new Set(["section"]);
		case "section":
		case "itemContainer":
			return new Set(["itemContainer", ...LEAF_ITEM_TYPES]);
		default:
			return new Set();
	}
}

function assertStringField(value, fieldName, contextLabel, { allowEmpty = false } = {}) {
	if (!_.isString(value)) {
		throw new Error(`${contextLabel} must include a string ${fieldName}.`);
	}
	if (!allowEmpty && _.trim(value) === "") {
		throw new Error(`${contextLabel} must include a non-empty ${fieldName}.`);
	}
}

function assertRootChildrenConsistency(children = [], contextLabel = "nodes", options = {}) {
	if (!Array.isArray(children) || children.length === 0) {
		return;
	}

	const existingChildren = Array.isArray(options.existingChildren) ? options.existingChildren : [];
	let expectedRootClass = existingChildren.length > 0
		? getRootChildStructuralClass(_.get(existingChildren, "[0].type", ""))
		: "";

	children.forEach((childNode, index) => {
		const childType = _.get(childNode, "type", "");
		const childRootClass = getRootChildStructuralClass(childType);
		if (!childRootClass) {
			throw new Error(`${contextLabel}[${index}] cannot be placed under parent type "${ROOT_TYPE}".`);
		}
		if (!expectedRootClass) {
			expectedRootClass = childRootClass;
			return;
		}
		if (childRootClass !== expectedRootClass) {
			throw new Error(`${contextLabel}[${index}] must match the checklist root child type "${expectedRootClass}".`);
		}
	});
}

function assertValidChildPlacement(parentType, childType, contextLabel, options = {}) {
	const allowedChildTypes = getAllowedChildTypes(parentType, options);
	if (!allowedChildTypes.has(childType)) {
		throw new Error(`${contextLabel} cannot be placed under parent type "${parentType}".`);
	}
}

function assertStructureNodeShape(node, parentType, contextLabel) {
	if (!_.isPlainObject(node)) {
		throw new Error(`${contextLabel} must be an object.`);
	}
	if (!_.isString(node.type) || _.trim(node.type) === "") {
		throw new Error(`${contextLabel} must include a type.`);
	}
	if (!isSupportedStructureType(node.type)) {
		throw new Error(`${contextLabel} has unsupported type "${node.type}".`);
	}
	if (parentType) {
		assertValidChildPlacement(parentType, node.type, contextLabel);
	}

	if (node.type === ROOT_TYPE) {
		assertStringField(node.name, "name", contextLabel);
		if (!_.isArray(node.children)) {
			throw new Error(`${contextLabel} must include a children array.`);
		}
	}

	if (node.type === "list" || node.type === "section") {
		assertStringField(node.name, "name", contextLabel);
		if (!_.isArray(node.children)) {
			throw new Error(`${contextLabel} must include a children array.`);
		}
	}
	if (node.type === "itemContainer") {
		assertStringField(node.label1, "label1", contextLabel, { allowEmpty: true });
		if (!_.isArray(node.children)) {
			throw new Error(`${contextLabel} must include a children array.`);
		}
		if (node.hasOwnProperty("label2") && !_.isString(node.label2)) {
			throw new Error(`${contextLabel} label2 must be a string when provided.`);
		}
	}
	if (isLeafItemType(node.type)) {
		assertStringField(node.label1, "label1", contextLabel, { allowEmpty: true });
		if (node.hasOwnProperty("label2") && !_.isString(node.label2)) {
			throw new Error(`${contextLabel} label2 must be a string when provided.`);
		}
		if (node.hasOwnProperty("labelOnly")) {
			if (node.type !== "item") {
				throw new Error(`${contextLabel} can only use labelOnly when type is "item".`);
			}
			if (!_.isBoolean(node.labelOnly)) {
				throw new Error(`${contextLabel} labelOnly must be a boolean when provided.`);
			}
		}
		if (node.hasOwnProperty("children")) {
			throw new Error(`${contextLabel} is a leaf node and cannot include children.`);
		}
	}
}

function compileStructureNodeForChecklist(node, parentType, contextLabel = "Node") {
	assertStructureNodeShape(node, parentType, contextLabel);

	const compiled = {
		id: _.isString(node.id) ? node.id : "",
		guid: _.isString(node.guid) && _.trim(node.guid) !== "" ? node.guid : uuidv4(),
		type: node.type
	};

	if (node.type === ROOT_TYPE) {
		compiled.name = node.name;
		compiled.children = node.children.map((childNode, index) => compileStructureNodeForChecklist(childNode, ROOT_TYPE, `${contextLabel}.children[${index}]`));
		assertRootChildrenConsistency(compiled.children, `${contextLabel}.children`);
		return compiled;
	}

	if (node.type === "list" || node.type === "section") {
		compiled.name = node.name;
		compiled.children = node.children.map((childNode, index) => compileStructureNodeForChecklist(childNode, node.type, `${contextLabel}.children[${index}]`));
		return compiled;
	}

	if (node.type === "itemContainer") {
		compiled.label1 = node.label1;
		if (node.hasOwnProperty("label2")) {
			compiled.label2 = node.label2;
		}
		compiled.children = node.children.map((childNode, index) => compileStructureNodeForChecklist(childNode, node.type, `${contextLabel}.children[${index}]`));
		return compiled;
	}

	compiled.label1 = node.label1;
	if (node.hasOwnProperty("label2")) {
		compiled.label2 = node.label2;
	}
	if (node.hasOwnProperty("labelOnly")) {
		compiled.labelOnly = node.labelOnly;
	}
	return compiled;
}

function projectChecklistNodeToStructure(node, parentType = ROOT_TYPE, contextLabel = "Node") {
	if (!_.isPlainObject(node)) {
		throw new Error(`${contextLabel} must be an object.`);
	}
	if (!_.isString(node.type) || _.trim(node.type) === "") {
		throw new Error(`${contextLabel} must include a type.`);
	}
	if (!isSupportedStructureType(node.type)) {
		throw new Error(`${contextLabel} has unsupported type "${node.type}".`);
	}
	if (parentType !== ROOT_TYPE) {
		assertValidChildPlacement(parentType, node.type, contextLabel);
	}

	const projected = {
		type: node.type
	};

	if (_.isString(node.id)) {
		projected.id = node.id;
	}
	if (_.isString(node.guid) && node.guid !== "") {
		projected.guid = node.guid;
	}

	if (node.type === "list" || node.type === "section") {
		assertStringField(node.name, "name", contextLabel);
		projected.name = node.name;
		const childNodes = _.isArray(node.children) ? node.children : [];
		projected.children = childNodes.map((childNode, index) => projectChecklistNodeToStructure(childNode, node.type, `${contextLabel}.children[${index}]`));
		return projected;
	}

	if (node.type === "itemContainer") {
		assertStringField(node.label1, "label1", contextLabel, { allowEmpty: true });
		projected.label1 = node.label1;
		if (node.hasOwnProperty("label2") && _.isString(node.label2)) {
			projected.label2 = node.label2;
		}
		const childNodes = _.isArray(node.children) ? node.children : [];
		projected.children = childNodes.map((childNode, index) => projectChecklistNodeToStructure(childNode, node.type, `${contextLabel}.children[${index}]`));
		return projected;
	}

	assertStringField(node.label1, "label1", contextLabel, { allowEmpty: true });
	projected.label1 = node.label1;
	if (node.hasOwnProperty("label2") && _.isString(node.label2)) {
		projected.label2 = node.label2;
	}
	if (node.type === "item" && _.isBoolean(node.labelOnly)) {
		projected.labelOnly = node.labelOnly;
	}
	return projected;
}

function buildTargetDescriptor(node, parentNode, parentChildren, index, path) {
	return {
		node,
		parentNode,
		parentChildren,
		index,
		path
	};
}

function resolveNodeByPath(rootChecklist, path) {
	if (!_.isArray(path)) {
		throw new Error("Target path must be an array.");
	}
	if (path.length === 0) {
		return buildTargetDescriptor(rootChecklist, null, null, -1, []);
	}

	let currentNode = rootChecklist;
	let parentNode = null;
	let parentChildren = null;
	for (let i = 0; i < path.length; i++) {
		const index = path[i];
		if (!Number.isInteger(index) || index < 0) {
			throw new Error(`Target path contains invalid index at position ${i}.`);
		}
		const currentChildren = _.isArray(currentNode.children) ? currentNode.children : [];
		if (index >= currentChildren.length) {
			throw new Error(`Target path index ${index} is out of range at position ${i}.`);
		}
		parentNode = currentNode;
		parentChildren = currentChildren;
		currentNode = currentChildren[index];
	}

	return buildTargetDescriptor(currentNode, parentNode, parentChildren, _.last(path), path);
}

function resolveNodeByPredicate(rootChecklist, predicate, modeLabel) {
	if (predicate(rootChecklist)) {
		return buildTargetDescriptor(rootChecklist, null, null, -1, []);
	}

	function walk(parentNode, path = []) {
		const childNodes = _.isArray(parentNode.children) ? parentNode.children : [];
		for (let i = 0; i < childNodes.length; i++) {
			const childNode = childNodes[i];
			const childPath = [...path, i];
			if (predicate(childNode)) {
				return buildTargetDescriptor(childNode, parentNode, childNodes, i, childPath);
			}
			if (canNodeHaveChildren(childNode.type)) {
				const nestedMatch = walk(childNode, childPath);
				if (nestedMatch) {
					return nestedMatch;
				}
			}
		}
		return null;
	}

	const match = walk(rootChecklist, []);
	if (!match) {
		throw new Error(`Unable to resolve target by ${modeLabel}.`);
	}
	return match;
}

function resolvePatchTarget(rootChecklist, target, options = {}) {
	if (!_.isPlainObject(target)) {
		throw new Error("Patch operation target must be an object.");
	}

	let resolvedTarget = target;
	if (target.mode === "selected") {
		if (_.isArray(options.selectedPath)) {
			resolvedTarget = {
				mode: "path",
				path: options.selectedPath
			};
		} else if (_.isString(options.selectedGuid) && _.trim(options.selectedGuid) !== "") {
			resolvedTarget = {
				mode: "guid",
				guid: options.selectedGuid
			};
		} else if (_.isString(options.selectedId) && _.trim(options.selectedId) !== "") {
			resolvedTarget = {
				mode: "id",
				id: options.selectedId
			};
		} else {
			throw new Error("Selected-node targeting requires options.selectedPath, options.selectedGuid, or options.selectedId.");
		}
	}

	if (resolvedTarget.mode === "path") {
		return resolveNodeByPath(rootChecklist, resolvedTarget.path);
	}
	if (resolvedTarget.mode === "guid") {
		return resolveNodeByPredicate(rootChecklist, (node) => _.isString(node.guid) && node.guid === resolvedTarget.guid, `guid "${resolvedTarget.guid}"`);
	}
	if (resolvedTarget.mode === "id") {
		return resolveNodeByPredicate(rootChecklist, (node) => _.isString(node.id) && node.id === resolvedTarget.id, `id "${resolvedTarget.id}"`);
	}

	throw new Error(`Unsupported target mode "${resolvedTarget.mode}".`);
}

function assertOperationHasChildrenTarget(targetDescriptor, op) {
	if (!canNodeHaveChildren(targetDescriptor.node.type)) {
		throw new Error(`Operation "${op}" requires a target node that can contain children.`);
	}
}

function compileNodesForParent(nodes, parentType, contextPrefix, options = {}) {
	if (!_.isArray(nodes) || nodes.length === 0) {
		throw new Error(`${contextPrefix} must include a non-empty nodes array.`);
	}
	const compiledNodes = nodes.map((node, index) => compileStructureNodeForChecklist(node, parentType, `${contextPrefix}[${index}]`));
	if (parentType === ROOT_TYPE) {
		assertRootChildrenConsistency(compiledNodes, contextPrefix, options);
	}
	return compiledNodes;
}

export function projectChecklistToStructureV1(checklist = {}) {
	if (!_.isPlainObject(checklist)) {
		throw new Error("Checklist must be an object.");
	}
	assertStringField(checklist.name, "name", "Checklist");

	const projected = {
		type: ROOT_TYPE,
		name: checklist.name,
		children: []
	};

	if (_.isString(checklist.id)) {
		projected.id = checklist.id;
	}
	if (_.isString(checklist.guid) && checklist.guid !== "") {
		projected.guid = checklist.guid;
	}

	const childNodes = _.isArray(checklist.children) ? checklist.children : [];
	projected.children = childNodes.map((node, index) => projectChecklistNodeToStructure(node, ROOT_TYPE, `Checklist.children[${index}]`));
	return projected;
}

export function applyStructurePatchV1(checklist = {}, patch = {}, options = {}) {
	if (!_.isPlainObject(checklist)) {
		throw new Error("Checklist must be an object.");
	}
	if (!_.isPlainObject(patch)) {
		throw new Error("Patch must be an object.");
	}
	if (patch.version !== 1) {
		throw new Error("Only structure patch version 1 is supported.");
	}
	if (!_.isArray(patch.operations) || patch.operations.length === 0) {
		throw new Error("Patch must include a non-empty operations array.");
	}

	const nextChecklist = _.cloneDeep(checklist);
	if (!_.isArray(nextChecklist.children)) {
		nextChecklist.children = [];
	}
	if (!_.isString(nextChecklist.type) || _.trim(nextChecklist.type) === "") {
		nextChecklist.type = ROOT_TYPE;
	}

	patch.operations.forEach((operation, operationIndex) => {
		if (!_.isPlainObject(operation)) {
			throw new Error(`Operation ${operationIndex} must be an object.`);
		}
		if (!_.isString(operation.op) || _.trim(operation.op) === "") {
			throw new Error(`Operation ${operationIndex} must include an op.`);
		}
		const contextPrefix = `operations[${operationIndex}].nodes`;
		const targetDescriptor = resolvePatchTarget(nextChecklist, operation.target, options);

		switch (operation.op) {
			case "appendChildren": {
				assertOperationHasChildrenTarget(targetDescriptor, operation.op);
				const compiledNodes = compileNodesForParent(operation.nodes, targetDescriptor.node.type, contextPrefix, {
					existingChildren: _.isArray(targetDescriptor.node.children) ? targetDescriptor.node.children : []
				});
				if (!_.isArray(targetDescriptor.node.children)) {
					targetDescriptor.node.children = [];
				}
				targetDescriptor.node.children.push(...compiledNodes);
				break;
			}
			case "replaceChildren": {
				assertOperationHasChildrenTarget(targetDescriptor, operation.op);
				const compiledNodes = compileNodesForParent(operation.nodes, targetDescriptor.node.type, contextPrefix);
				targetDescriptor.node.children = compiledNodes;
				break;
			}
			case "insertBefore": {
				if (!targetDescriptor.parentNode || !targetDescriptor.parentChildren) {
					throw new Error("Operation \"insertBefore\" cannot target the checklist root.");
				}
				const compiledNodes = compileNodesForParent(operation.nodes, targetDescriptor.parentNode.type || ROOT_TYPE, contextPrefix, {
					existingChildren: targetDescriptor.parentChildren
				});
				targetDescriptor.parentChildren.splice(targetDescriptor.index, 0, ...compiledNodes);
				break;
			}
			case "insertAfter": {
				if (!targetDescriptor.parentNode || !targetDescriptor.parentChildren) {
					throw new Error("Operation \"insertAfter\" cannot target the checklist root.");
				}
				const compiledNodes = compileNodesForParent(operation.nodes, targetDescriptor.parentNode.type || ROOT_TYPE, contextPrefix, {
					existingChildren: targetDescriptor.parentChildren
				});
				targetDescriptor.parentChildren.splice(targetDescriptor.index + 1, 0, ...compiledNodes);
				break;
			}
			case "replaceNode": {
				if (!targetDescriptor.parentNode || !targetDescriptor.parentChildren) {
					throw new Error("Operation \"replaceNode\" cannot target the checklist root.");
				}
				const compiledNodes = compileNodesForParent(operation.nodes, targetDescriptor.parentNode.type || ROOT_TYPE, contextPrefix, {
					existingChildren: targetDescriptor.parentChildren
				});
				targetDescriptor.parentChildren.splice(targetDescriptor.index, 1, ...compiledNodes);
				break;
			}
			default:
				throw new Error(`Unsupported operation "${operation.op}".`);
		}
	});

	return nextChecklist;
}
